/**
 * DeadlineChip — 149-cw-r137 (2026-08-31).
 *
 * Compact urgency chip untuk task deadline.
 *   - Overdue           → red solid  (deadline sudah lewat)
 *   - Hari Ini          → red solid  (jatuh tempo hari ini)
 *   - H-1               → rose       (1 hari lagi)
 *   - H-2 / H-3         → amber      (kritikal)
 *   - H-4..7            → navy       (perhatian)
 *   - H-8+              → slate      (aman)
 *   - Terminal (done/cancelled) atau tanpa deadline → hidden (return null)
 *
 * Props:
 *   deadline: ISO string atau null
 *   status:   task.status
 *   size:     "sm" (default) | "xs" — dipakai di table utk lebih compact
 */
import { fmtDate } from "@/lib/constants";

function _daysUntil(deadlineISO) {
  if (!deadlineISO) return null;
  const d = new Date(deadlineISO);
  if (isNaN(d.getTime())) return null;
  // Normalize both ke midnight lokal supaya "H-0" = jatuh tempo hari ini.
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date();
  const todayNoon = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((target.getTime() - todayNoon.getTime()) / (1000 * 60 * 60 * 24));
}

function _tone(days) {
  if (days < 0) return { bg: "bg-rose-600", text: "text-white", border: "border-rose-700", ring: "" };
  if (days === 0) return { bg: "bg-rose-600", text: "text-white", border: "border-rose-700", ring: "ring-1 ring-rose-300 animate-pulse" };
  if (days === 1) return { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-300", ring: "" };
  if (days <= 3) return { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-400", ring: "" };
  if (days <= 7) return { bg: "bg-[#0B1D3A]", text: "text-white", border: "border-[#0B1D3A]", ring: "" };
  return { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", ring: "" };
}

function _label(days) {
  if (days < 0) return `Terlambat ${Math.abs(days)} hari`;
  if (days === 0) return "Hari Ini";
  return `H-${days}`;
}

export default function DeadlineChip({ deadline, status, size = "sm" }) {
  // Kalau task sudah terminal, tidak perlu urgency chip.
  if (status === "selesai" || status === "dibatalkan") return null;
  const days = _daysUntil(deadline);
  if (days === null) return null;

  const tone = _tone(days);
  const sizeCls = size === "xs"
    ? "px-1 py-0 text-[9px] tracking-normal"
    : "px-1.5 py-0.5 text-[9px] tracking-wider";

  return (
    <span
      data-testid="deadline-chip"
      data-days={days}
      title={`Deadline: ${fmtDate(deadline)}${days < 0 ? ` · Terlambat ${Math.abs(days)} hari` : days === 0 ? " · Jatuh tempo hari ini" : ` · ${days} hari lagi`}`}
      className={`inline-flex items-center gap-0.5 font-semibold uppercase border tabular-nums ${sizeCls} ${tone.bg} ${tone.text} ${tone.border} ${tone.ring}`}
    >
      {_label(days)}
    </span>
  );
}
